import React, { Fragment, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { notification } from 'antd';

import { getCampDetailService, editCampDetailService } from '../../services/userServices';
import { uploadMedia } from '../../services/campersServices';
import { ProgramLoader, PDFIcon } from '../reuse/SVG';
import { addBr, getBr } from '../../helper';

// The camp's staff agreement text and training guide PDF, and the two buttons that open them.
//
// Lifted out of Counselors.js so the Form Builder can own them — they configure the staff
// application, not the personnel list, and on the personnel page they sat behind
// `counselorList.length > 0`, which left a camp with no staff yet unable to set an agreement at
// all. Counselors.js still renders this with the legacy staff form, where there is no Form
// Builder route to move them to.
//
// Both values live on the camp record and are read back by the application itself: the
// `trainingGuidePdf` and `readTrainingGuide` questions in staffFormSchema.js render from
// campDetail (see QuestionField).
const CampAgreementControls = ({ campCode, variant = 'legacy' }) => {
    const [agreementModal, setAgreementModal] = useState(false);
    const [trainingGuideModal, setTrainingGuideModal] = useState(false);
    const [agreement, setAgreement] = useState('');
    const [trainingGuidePdf, setTrainingGuidePdf] = useState('');
    const [isNewTrainingGuidePdf, setIsNewTrainingGuidePdf] = useState(true);
    const [trainingGuideUploadLoader, setTrainingGuideUploadLoader] = useState(false);

    const getCampDetail = () => {
        if (!campCode) return;
        getCampDetailService(`?campCode=${campCode}`).then(res => {
            if (res?.data?.statusCode) {
                let tempAgreement = res.data.responseData.result?.agreement;
                let tempTrainingGuidePdf = res.data.responseData.result?.trainingGuidePdf;
                if (tempAgreement) {
                    setAgreement(getBr(tempAgreement))
                }
                if (tempTrainingGuidePdf) {
                    setTrainingGuidePdf(tempTrainingGuidePdf);
                    setIsNewTrainingGuidePdf(false);
                }
            }
        })
    }

    useEffect(() => {
        getCampDetail();
    }, [campCode])

    // One submit for both modals — the endpoint takes the pair, so whichever modal is open sends
    // the other's current value back unchanged.
    const handleAgreementSubmit = e => {
        e.preventDefault();
        setTrainingGuideUploadLoader(true);
        let params = {
            campCode: campCode,
            trainingGuidePdf: trainingGuidePdf,
            agreement: addBr(agreement)
        }

        editCampDetailService(params).then(res => {
            setTrainingGuideUploadLoader(false);
            if (trainingGuidePdf) {
                setIsNewTrainingGuidePdf(false);
            }
            if (res?.data?.statusCode) {
                getCampDetail();
                notification.open({
                    description: 'Data updated successfully',
                });
            }

            setAgreementModal(false)
            setTrainingGuideModal(false)
        })
    }

    const handleUploadMedia = e => {
        const data = new FormData();
        setTrainingGuideUploadLoader(true);
        data.append('file', e.target.files[0]);
        uploadMedia(data).then((res => {
            setTrainingGuideUploadLoader(false);
            if (res.data.responseData.result.fileUrl !== undefined) {
                setTrainingGuidePdf(res.data.responseData.result.fileUrl);
            }
        }))
    }

    // Closing without saving throws away a PDF uploaded in this session, but keeps the one
    // already stored on the camp.
    const handleClose = () => {
        setTrainingGuideModal(false);
        if (isNewTrainingGuidePdf) {
            setTrainingGuidePdf('')
        }
    }

    // Only the trigger buttons change between hosts: bootstrap on the personnel page, the Form
    // Builder's outline buttons on its Tailwind header. The modals are react-bootstrap in both —
    // they portal to <body> and the app's global CSS is loaded on either route.
    const buttonClass = variant === 'builder'
        ? 'tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-sand-300 tw-px-3.5 tw-py-2.5 tw-text-xs tw-font-semibold tw-text-gray-700 tw-transition hover:tw-bg-sand-100'
        : 'btn btn-primary me-2';

    return (
        <Fragment>
            <button type="button" className={buttonClass} onClick={() => setAgreementModal(true)}>
                <i className="icon-doc-check" /> Agreement
            </button>
            <button type="button" className={buttonClass} onClick={() => setTrainingGuideModal(true)}>
                <i className="icon-upload" /> Upload Training Guide
            </button>

            <Modal
                show={agreementModal}
                animation={false}
                dialogClassName="folder_model"
                size="lg"
                centered
            >
            <Modal.Body>
                <span className="modal_close" onClick={() => setAgreementModal(false)}><i className="icon-cross" /></span>
                <div className="text-center p-5 pickup_modal_body">
                    <div className="text-center p-5 pickup_modal_body">
                        <p>Edit Agreement</p>
                    </div>
                    <form onSubmit={handleAgreementSubmit}>
                        <div className="form-group trainingGuideUploadLoader">
                        {trainingGuideUploadLoader ? <div className="page_loader"><ProgramLoader /></div> : ''}
                            <textarea
                                value={agreement}
                                onChange={e => setAgreement(e.target.value)}
                                className="form-control cm_text_area"></textarea>
                        </div>

                        {agreement ? <button type="submit" className="btn btn-primary w-100">Save</button> : ''}
                    </form>

                </div>
            </Modal.Body>
            </Modal>


            <Modal
                show={trainingGuideModal}
                animation={false}
                dialogClassName="folder_model"
                size="lg"
                centered
            >
            <Modal.Body>
                <span className="modal_close" onClick={handleClose}><i className="icon-cross" /></span>
                <div className="text-center p-5 pickup_modal_body">
                    <div className="text-center p-5 pb-0 pickup_modal_body">
                        <p>Upload Training Guide</p>
                    </div>
                    <form onSubmit={handleAgreementSubmit}>
                    <div className={`icon_upload_wrapper upload_2 ${trainingGuidePdf ? 'uploaded' : ''}`}>

                        {trainingGuideUploadLoader ? <div className="page_loader"><ProgramLoader /></div> : ''}
                        <input type="file" accept=".pdf,.doc" onChange={handleUploadMedia} value="" />
                        <div>
                            <i className="icon-upload" />
                            <p>Upload PDF</p>
                        </div>
                    </div>
                    {trainingGuidePdf ? <Fragment>
                        <div className="trainingGuidePdf"><a target="_blank" rel="noreferrer" href={trainingGuidePdf}><PDFIcon /> {trainingGuidePdf ? trainingGuidePdf.slice(trainingGuidePdf.lastIndexOf('_') + 1) : ''}</a> <span className="icon-close" onClick={() => setTrainingGuidePdf('')} /></div>
                        <button type="submit" className="btn btn-primary w-100">Save</button>
                    </Fragment> : ''}
                    </form>

                </div>
            </Modal.Body>
            </Modal>
        </Fragment>
    );
};

export default CampAgreementControls;
